import * as path from "node:path";
import path__default from "node:path";
import * as fs from "node:fs";
import { inject, defineComponent, ref, onMounted } from "vue";
import { renderToString } from "@vue/server-renderer";
import { renderSSRHead } from "@unhead/ssr";
import chalk from "chalk";
function defer() {
  const deferred = { status: "pending" };
  deferred.promise = new Promise((resolve, reject) => {
    deferred.resolve = (value) => {
      deferred.status = "resolved";
      return resolve(value);
    };
    deferred.reject = (error) => {
      deferred.status = "rejected";
      return reject(error);
    };
  });
  return deferred;
}
function isRedirect(redirect) {
  return Boolean(
    redirect.status && redirect.status >= 300 && redirect.status < 400
  );
}
function useServerRedirect() {
  const deferred = defer();
  const redirectLocation = {};
  return {
    deferred,
    redirectLocation,
    isRedirect: () => isRedirect(redirectLocation),
    redirect: async (location, status = 302) => {
      const newLocation = {
        url: location,
        status
      };
      Object.assign(redirectLocation, newLocation);
      if (isRedirect(redirectLocation)) {
        deferred.resolve("");
      }
    }
  };
}
function useClientRedirect(spaRedirect) {
  return async (location, status) => {
    if (location.startsWith("/")) {
      await spaRedirect(location, status);
    } else {
      window.location.href = location;
    }
  };
}
const REDIRECT_SYMBOL_WANNABE = `${"wucdbm-vite-vue-ssr"}.redirect`;
function provideRedirect(app, redirect) {
  app.provide(REDIRECT_SYMBOL_WANNABE, redirect);
}
function useRedirect() {
  return inject(REDIRECT_SYMBOL_WANNABE);
}
const UNSAFE_CHARS_REGEXP = /[<>'/\u2028\u2029]/g;
const ESCAPED_CHARS = {
  // eslint-disable-next-line quotes
  "'": "\\'",
  "<": "\\u003C",
  ">": "\\u003E",
  "/": "\\u002F",
  "\u2028": "\\u2028",
  "\u2029": "\\u2029"
};
function escapeUnsafeChars(unsafeChar) {
  return ESCAPED_CHARS[unsafeChar];
}
function serializeState(state) {
  try {
    return JSON.stringify(JSON.stringify(state || {})).replace(
      UNSAFE_CHARS_REGEXP,
      escapeUnsafeChars
    );
  } catch (error) {
    console.error("[SSR] On state serialization -", error, state);
    return "{}";
  }
}
function deserializeState(state) {
  try {
    return JSON.parse(state || "{}");
  } catch (error) {
    console.error("[SSR] On state deserialization -", error, state);
    return {};
  }
}
function createSetStatusState() {
  let status = void 0;
  const setStatus = (s) => {
    status = s;
  };
  return {
    get status() {
      return status;
    },
    setStatus
  };
}
const STATUS_SYMBOL_WANNABE = `${"wucdbm-vite-vue-ssr"}.status`;
function provideSetStatus(app, statusState) {
  app.provide(STATUS_SYMBOL_WANNABE, statusState);
}
function useSetStatus() {
  return inject(STATUS_SYMBOL_WANNABE);
}
async function renderApp({
  factory,
  replacer,
  path: path2,
  ssrManifest,
  hooks
}) {
  const {
    app,
    router,
    baseUrl,
    getState,
    head,
    preload,
    preLoaders,
    getCookies
  } = await factory();
  const preLoadersWithDefault = createPreLoaders(preLoaders || {});
  const { deferred, redirectLocation, redirect, isRedirect: isRedirect2 } = useServerRedirect();
  provideRedirect(app, redirect);
  const statusState = createSetStatusState();
  provideSetStatus(app, statusState);
  if (baseUrl && path2.startsWith(baseUrl)) {
    path2 = path2.slice(baseUrl.length);
  }
  await router.push(path2);
  await router.isReady();
  if (isRedirect2()) {
    const responseData2 = hooks && hooks.getResponseData && await hooks.getResponseData();
    return returnRedirect(redirectLocation, responseData2);
  }
  if (hooks && hooks.onRouterReady) {
    await hooks.onRouterReady();
  }
  const ssrContext = {};
  renderToString(app, ssrContext).then(deferred.resolve).catch(deferred.reject);
  const body = await deferred.promise;
  if (isRedirect2()) {
    const responseData2 = hooks && hooks.getResponseData && await hooks.getResponseData();
    return returnRedirect(redirectLocation, responseData2);
  }
  let headHtmlResult = await renderHead(head);
  const dependencies = ssrManifest ? findDependencies(ssrContext.modules, ssrManifest) : [];
  if (preload && dependencies.length > 0) {
    const preloadLinks = createPreloadLinks(
      dependencies,
      preLoadersWithDefault
    ).join("\n");
    headHtmlResult = {
      ...headHtmlResult,
      headTags: `${headHtmlResult.headTags}
${preloadLinks}`
    };
  }
  const initialState = serializeState(getState());
  const coreDependencies = JSON.parse(
    '{"__WITE_CORE_DEPS__":[]}'
  ).__WITE_CORE_DEPS__;
  const responseData = hooks && hooks.getResponseData && await hooks.getResponseData();
  return {
    redirectLocation,
    dependencies,
    coreDependencies,
    preLoaders: preLoadersWithDefault,
    headHtmlResult,
    body,
    initialState,
    status: statusState.status,
    html: replacer(headHtmlResult, body, initialState),
    data: responseData,
    ssrContext,
    cookies: getCookies == null ? void 0 : getCookies()
  };
}
async function renderHead(head) {
  if (head) {
    return await renderSSRHead(head);
  }
  return {
    headTags: "",
    htmlAttrs: "",
    bodyAttrs: "",
    bodyTags: "",
    bodyTagsOpen: ""
  };
}
function returnRedirect(redirectLocation, data) {
  return {
    redirectLocation,
    status: redirectLocation.status,
    dependencies: [],
    coreDependencies: [],
    preLoaders: {},
    headHtmlResult: {
      htmlAttrs: "",
      headTags: "",
      bodyAttrs: "",
      bodyTags: "",
      bodyTagsOpen: ""
    },
    body: "",
    initialState: "undefined",
    html: "",
    data,
    ssrContext: {},
    cookies: void 0
  };
}
function findDependencies(modules, manifest) {
  const files = /* @__PURE__ */ new Set();
  for (const id of modules || []) {
    for (const file of manifest[id] || []) {
      files.add(file);
    }
  }
  return [...files];
}
const defaultPreLoaders = {
  js: {
    link: (path2) => `<link rel="modulepreload" crossorigin href="${path2}">`,
    header: (path2) => `<${path2}>; rel=preload; as=script`
  },
  css: {
    link: (path2) => `<link rel="stylesheet" href="${path2}">`,
    header: (path2) => `<${path2}>; rel=preload; as=style`
  },
  svg: {
    link: (path2) => `<link rel="preload" href="${path2}" as="image" type="image/svg+xml" />`,
    header: (path2) => `<${path2}>; rel=preload; as=image`
  }
};
function createPreLoaders(loaders) {
  return {
    ...defaultPreLoaders,
    ...loaders
  };
}
function createPreloadLinks(files, loaders) {
  return createLinks(files, loaders, "link");
}
function createPreloadHeaders(files, loaders) {
  return createLinks(files, loaders, "header");
}
function createLinks(files, loaders, func) {
  const links = [];
  for (const file of files || []) {
    const parts = file.split(".");
    const extension = parts[parts.length - 1];
    const loader = loaders[extension];
    if (loader) {
      links.push(loader[func](file));
    }
  }
  return links;
}
function getExpressPath(request) {
  if (request.originalUrl) {
    return request.originalUrl;
  }
  if (request.url) {
    return request.url;
  }
  return "/";
}
const createSSRDevHandler = async (server, options) => {
  const resolveFilePath = (p) => path.resolve(server.config.root, p);
  async function getIndexTemplate(url) {
    const indexHtml = fs.readFileSync(
      options.input || resolveFilePath("index.html"),
      "utf-8"
    );
    return await server.transformIndexHtml(url, indexHtml);
  }
  return async (request, response, next) => {
    var _a, _b, _c, _d, _e, _f;
    if (request.method !== "GET" || request.originalUrl === "/favicon.ico") {
      return next();
    }
    if ((_a = request.originalUrl) == null ? void 0 : _a.startsWith("/@vite")) {
      return next();
    }
    if ((_b = request.originalUrl) == null ? void 0 : _b.startsWith("/src")) {
      return next();
    }
    if ((_c = request.originalUrl) == null ? void 0 : _c.startsWith("/node_modules")) {
      return next();
    }
    if ((_d = request.originalUrl) == null ? void 0 : _d.startsWith("/@id")) {
      return next();
    }
    if ((_e = request.originalUrl) == null ? void 0 : _e.startsWith("/_hmr")) {
      return next();
    }
    let template;
    try {
      template = await getIndexTemplate(request.originalUrl);
    } catch (error) {
      logServerError(error, server);
      return next(error);
    }
    try {
      const entryPoint = resolveFilePath(
        options.entryServer || "/src/entry-server.ts"
      );
      let resolvedEntryPoint = await server.ssrLoadModule(
        resolveFilePath(entryPoint)
      );
      resolvedEntryPoint = resolvedEntryPoint.default || resolvedEntryPoint;
      const render = resolvedEntryPoint.render || resolvedEntryPoint;
      const entryData = ((_f = options.ssr) == null ? void 0 : _f.serverEntryData) && options.ssr.serverEntryData(request) || void 0;
      const path2 = getExpressPath(request);
      const rendered = await render(path2, entryData, template);
      const { redirectLocation, status, cookies, ssrContext } = rendered;
      let { html } = rendered;
      if (isRedirect(redirectLocation)) {
        response.statusCode = redirectLocation.status;
        response.setHeader("Location", redirectLocation.url);
        return response.end();
      }
      if (ssrContext.teleports) {
        Object.entries(ssrContext.teleports).forEach((v) => {
          if (v[0].indexOf("#teleport-") !== 0) {
            return;
          }
          const id = v[0].substring(1);
          const regExString = `<div id="${id}"([a-zA-Z\\s]+)*([^>]+)*(?:>(.*?)</div>)`;
          const regExp = new RegExp(regExString, "g");
          html = html.replaceAll(
            regExp,
            `<div id="${id}"$1$2>$3${v[1]}</div>`
          );
        });
      }
      if (cookies) {
        response.setHeader("Set-Cookie", cookies);
      }
      response.writeHead(status || 200, {
        "Content-Type": "text/html",
        "Cache-Control": "no-cache"
      });
      response.end(html);
    } catch (error) {
      response.setHeader("Content-Type", "text/html");
      response.end(template);
      logServerError(error, server);
    }
  };
};
const createPreviewHandler = (server, options) => {
  var _a, _b;
  const outDir = ((_a = server.config.build) == null ? void 0 : _a.outDir) ?? path.resolve(server.config.root, "dist/client");
  const indexHtmlPath = path.resolve(outDir, "index.html");
  let indexHtmlContents = fs.readFileSync(indexHtmlPath, "utf-8");
  if ((_b = options.preview) == null ? void 0 : _b.replacer) {
    indexHtmlContents = options.preview.replacer(indexHtmlContents);
  }
  return async (request, response, next) => {
    var _a2;
    if (request.method !== "GET" || request.originalUrl === "/favicon.ico") {
      return next();
    }
    const fileOnDisk = path.resolve(
      outDir,
      ((_a2 = request.originalUrl) == null ? void 0 : _a2.substring(1)) || ""
    );
    if ("/index.html" !== request.originalUrl && fs.existsSync(fileOnDisk)) {
      const stat = fs.lstatSync(fileOnDisk);
      if (!stat.isDirectory()) {
        return next();
      }
    }
    response.writeHead(200, {
      "Content-Type": "text/html",
      "Cache-Control": "no-cache"
    });
    return response.end(indexHtmlContents);
  };
};
const createLivenessAndReadinessHandler = (options) => {
  return async (request, response, next) => {
    var _a, _b;
    const readiness = (_a = options.probes) == null ? void 0 : _a.readiness;
    if (request.originalUrl === (readiness == null ? void 0 : readiness.path)) {
      response.writeHead((readiness == null ? void 0 : readiness.statusCode) || 204);
      return response.end();
    }
    const liveness = (_b = options.probes) == null ? void 0 : _b.liveness;
    if (request.originalUrl === (liveness == null ? void 0 : liveness.path)) {
      response.writeHead((liveness == null ? void 0 : liveness.statusCode) || 204);
      return response.end();
    }
    return next();
  };
};
function logServerError(error, server) {
  server.ssrFixStacktrace(error);
  const msg = buildErrorMessage(error, [
    chalk.red(`Internal server error: ${error.message}`)
  ]);
  server.config.logger.error(msg, {
    clear: true,
    timestamp: true,
    error
  });
  const sendError = () => server.ws.send({ type: "error", err: prepareError(error) });
  setTimeout(sendError, 100);
  setTimeout(sendError, 250);
}
const splitRE = /\r?\n/;
function pad(source, n = 2) {
  const lines = source.split(splitRE);
  return lines.map((l) => " ".repeat(n) + l).join("\n");
}
function prepareError(err) {
  return {
    message: stripAnsi(err.message),
    stack: stripAnsi(cleanStack(err.stack || "")),
    id: err.id,
    frame: stripAnsi(err.frame || ""),
    plugin: err.plugin,
    pluginCode: err.pluginCode,
    loc: err.loc
  };
}
function buildErrorMessage(err, args = [], includeStack = true) {
  if (err.plugin) {
    args.push(`  Plugin: ${chalk.magenta(err.plugin)}`);
  }
  if (err.id) {
    args.push(`  File: ${chalk.cyan(err.id)}`);
  }
  if (err.frame) {
    args.push(chalk.yellow(pad(err.frame)));
  }
  if (includeStack && err.stack) {
    args.push(pad(cleanStack(err.stack)));
  }
  return args.join("\n");
}
function cleanStack(stack) {
  return stack.split(/\n/g).filter((l) => /^\s*at/.test(l)).join("\n");
}
function ansiRegex({ onlyFirst = false } = {}) {
  const pattern = [
    "[\\u001B\\u009B][[\\]()#;?]*(?:(?:(?:(?:;[-a-zA-Z\\d\\/#&.:=?%@~_]+)*|[a-zA-Z\\d]+(?:;[-a-zA-Z\\d\\/#&.:=?%@~_]*)*)?\\u0007)",
    "(?:(?:\\d{1,4}(?:;\\d{0,4})*)?[\\dA-PR-TZcf-nq-uy=><~]))"
  ].join("|");
  return new RegExp(pattern, onlyFirst ? void 0 : "g");
}
function stripAnsi(string) {
  if (typeof string !== "string") {
    throw new TypeError(`Expected a \`string\`, got \`${typeof string}\``);
  }
  return string.replace(ansiRegex(), "");
}
function WucdbmViteVueSsr(options = {}) {
  return {
    name: "wucdbm-vite-vue-ssr",
    ["wucdbm-vite-vue-ssr"]: options,
    config(config, env) {
      var _a, _b;
      const conf = {
        ssr: {
          noExternal: ["wucdbm-vite-vue-ssr"]
        }
      };
      if ("serve" === env.command && env.isPreview) {
        if (!conf.build) {
          conf.build = {};
        }
        const distDir = ((_a = config.build) == null ? void 0 : _a.outDir) ?? path__default.resolve(process.cwd(), "dist");
        conf.build.outDir = path__default.resolve(distDir, "client");
        conf.preview = (_b = options.preview) == null ? void 0 : _b.options;
      }
      return conf;
    },
    configurePreviewServer(server) {
      return () => {
        server.middlewares.use(
          createLivenessAndReadinessHandler(options)
        );
        server.middlewares.use(createPreviewHandler(server, options));
      };
    },
    async configureServer(server) {
      var _a;
      server.middlewares.use(createLivenessAndReadinessHandler(options));
      if (process.env.DEV_SSR) {
        if (false === ((_a = options.ssr) == null ? void 0 : _a.nodeTlsRejectUnauthorized)) {
          process.env["NODE_TLS_REJECT_UNAUTHORIZED"] = "0";
        }
        const handler = await createSSRDevHandler(server, options);
        server.middlewares.use(handler);
      }
    }
  };
}
const ClientOnly = defineComponent({
  name: "ClientOnly",
  setup(_, { slots }) {
    const show = ref(false);
    onMounted(() => {
      show.value = true;
    });
    return () => show.value && slots.default ? slots.default() : null;
  }
});
export {
  ClientOnly,
  WucdbmViteVueSsr,
  createPreloadHeaders,
  createPreloadLinks,
  createSetStatusState,
  deserializeState,
  getExpressPath,
  provideRedirect,
  provideSetStatus,
  renderApp,
  serializeState,
  useClientRedirect,
  useRedirect,
  useSetStatus
};
